import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Book } from '../../types/book';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface BookFormProps {
  isOpen: boolean;
  onClose: () => void;
  onBookCreated: () => void;
  initialBook?: Book;
  isEditMode?: boolean;
}

const initialFormData: Omit<Book, 'id' | 'created_at' | 'updated_at' | 'chapters'> = {
  title: '',
  author: '',
  overall_summary: '',
  status: 'not_started',
  category: undefined,
  started_at: undefined,
  completed_at: undefined,
};

const BookForm: React.FC<BookFormProps> = ({
  isOpen,
  onClose,
  onBookCreated,
  initialBook = undefined,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialBook) {
        setFormData({
          title: initialBook.title,
          author: initialBook.author,
          overall_summary: initialBook.overall_summary,
          status: initialBook.status,
          category: initialBook.category,
          started_at: initialBook.started_at,
          completed_at: initialBook.completed_at,
        });
      } else {
        setFormData(initialFormData);
      }
    }
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300);
    }
  }, [isOpen, isEditMode, initialBook]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      if (isEditMode) {
        const response = await apiClient.put(`/api/books/${initialBook?.id}/`, formData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onBookCreated();
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to update book (${response.status})`);
        }
      } else {
        const response = await apiClient.post('/api/books/', formData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onBookCreated();
          setFormData(initialFormData);
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to create book (${response.status})`);
        }
      }
    } catch (err: unknown) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} book:`, err);
      const errorMsg = err instanceof Error ? err.message :
        `Failed to ${isEditMode ? 'update' : 'create'} book.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    backgroundColor: COLORS.bg.panel,
    color: COLORS.text.primary,
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        style={{ position: 'relative', zIndex: 10 }}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(107, 114, 128, 0.75)',
              transition: 'opacity 0.3s ease-out'
            }}
          />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, zIndex: 10, overflowY: 'auto' }}>
          <div
            style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
              textAlign: 'center'
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                style={{
                  position: 'relative',
                  transform: 'none',
                  overflow: 'hidden',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  transition: 'all 0.3s ease-out',
                  width: '100%',
                  maxWidth: '512px',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    paddingRight: SPACING[16],
                    paddingTop: SPACING[16]
                  }}
                >
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                    }}
                    onClick={onClose}
                  >
                    <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 0, textAlign: 'left', width: '100%' }}>
                    <Dialog.Title
                      as="h3"
                      style={{
                        fontSize: TYPOGRAPHY.headings.sizes.h2,
                        fontWeight: TYPOGRAPHY.weight.semibold,
                        lineHeight: 1.5,
                        color: COLORS.text.primary,
                        fontFamily: TYPOGRAPHY.headings.fontFamily,
                      }}
                    >
                      {isEditMode ? 'Edit Book' : 'Add New Book'}
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} style={{ marginTop: SPACING[16], display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                      <div>
                        <label htmlFor="title" style={labelStyle}>
                          Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="author" style={labelStyle}>
                          Author
                        </label>
                        <input
                          type="text"
                          name="author"
                          id="author"
                          value={formData.author}
                          onChange={handleInputChange}
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="overall_summary" style={labelStyle}>
                          Overall Summary
                        </label>
                        <textarea
                          name="overall_summary"
                          id="overall_summary"
                          rows={4}
                          value={formData.overall_summary}
                          onChange={handleInputChange}
                          style={{
                            ...inputStyle,
                            resize: 'vertical',
                          }}
                        />
                      </div>

                      <div>
                        <label htmlFor="status" style={labelStyle}>
                          Status
                        </label>
                        <select
                          name="status"
                          id="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          style={inputStyle}
                        >
                          <option value="not_started">Not Started</option>
                          <option value="in_progress">In Progress</option>
                          <option value="read">Read</option>
                          <option value="read_and_digested">Read and Digested</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="started_at" style={labelStyle}>
                          Started At
                        </label>
                        <input
                          type="date"
                          name="started_at"
                          id="started_at"
                          value={formData.started_at || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label htmlFor="completed_at" style={labelStyle}>
                          Completed At
                        </label>
                        <input
                          type="date"
                          name="completed_at"
                          id="completed_at"
                          value={formData.completed_at || ''}
                          onChange={handleInputChange}
                          style={inputStyle}
                        />
                      </div>

                      {submitError && (
                        <div
                          style={{
                            borderRadius: RADIUS,
                            backgroundColor: '#FEF2F2',
                            padding: SPACING[16]
                          }}
                        >
                          <div style={{ display: 'flex' }}>
                            <div style={{ flexShrink: 0 }}>
                              <ExclamationCircleIcon
                                style={{ height: '20px', width: '20px', color: COLORS.status.error }}
                                aria-hidden="true"
                              />
                            </div>
                            <div style={{ marginLeft: SPACING[12] }}>
                              <h3
                                style={{
                                  fontSize: TYPOGRAPHY.size.sm,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: '#991B1B'
                                }}
                              >
                                {submitError}
                              </h3>
                            </div>
                          </div>
                        </div>
                      )}

                      {submitSuccess && (
                        <div
                          style={{
                            borderRadius: RADIUS,
                            backgroundColor: '#ECFDF5',
                            padding: SPACING[16]
                          }}
                        >
                          <div style={{ display: 'flex' }}>
                            <div style={{ flexShrink: 0 }}>
                              <CheckCircleIcon
                                style={{ height: '20px', width: '20px', color: COLORS.status.success }}
                                aria-hidden="true"
                              />
                            </div>
                            <div style={{ marginLeft: SPACING[12] }}>
                              <h3
                                style={{
                                  fontSize: TYPOGRAPHY.size.sm,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: '#065F46'
                                }}
                              >
                                Book {isEditMode ? 'updated' : 'created'} successfully!
                              </h3>
                            </div>
                          </div>
                        </div>
                      )}

                      <div
                        style={{
                          marginTop: SPACING[24],
                          display: 'flex',
                          flexDirection: 'row-reverse',
                          gap: SPACING[12],
                        }}
                      >
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          style={{
                            display: 'inline-flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: RADIUS,
                            backgroundColor: isSubmitting ? COLORS.text.tertiary : COLORS.accent.default,
                            padding: `${SPACING[8]} ${SPACING[12]}`,
                            fontSize: TYPOGRAPHY.size.sm,
                            fontWeight: TYPOGRAPHY.weight.semibold,
                            color: COLORS.bg.panel,
                            border: 'none',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            opacity: isSubmitting ? 0.5 : 1,
                          }}
                        >
                          {isSubmitting ? (
                            <>
                              <ArrowPathIcon
                                style={{
                                  height: '20px',
                                  width: '20px',
                                  marginRight: SPACING[8],
                                  animation: 'spin 1s linear infinite',
                                }}
                              />
                              {isEditMode ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            isEditMode ? 'Update Book' : 'Create Book'
                          )}
                        </button>
                        <button
                          type="button"
                          style={{
                            display: 'inline-flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: RADIUS,
                            backgroundColor: COLORS.bg.panel,
                            padding: `${SPACING[8]} ${SPACING[12]}`,
                            fontSize: TYPOGRAPHY.size.sm,
                            fontWeight: TYPOGRAPHY.weight.semibold,
                            color: COLORS.text.primary,
                            border: `1px solid ${COLORS.border.default}`,
                            cursor: 'pointer',
                          }}
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BookForm;
